import type { Correlations } from "@/composables/forms/formTypes";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type { PipeFrictionProperties } from "@/libs/calculations/tubingFrictionLoss";
import { Casing_Flow_Options_Enum } from "gql/api";
import type {
  CasingIntervalGeometry,
  CasingPressureIntervalResult
} from "./types";
import {
  computeIntervalCasingPressureProperties,
  getEmptyCasingFrictionProperties,
  resolveCasingCorrelationOptions,
  computeCasingFrictionPropertiesAcrossEquipmentIntervalUpward
} from "./intervals";
import { resolveAnnulusDiameterForInterval } from "./geometry";

export function computeCasingPressurePropertiesBetweenPerfsAndDatum(
  wellFlowingPressureAtDatumPSIA: number,
  correlations: Correlations | null | undefined,
  datumPointMDFt: number,
  datumPointTVDFt: number,
  topOfPerforationsMDFt: number,
  topOfPerforationsTVDFt: number,
  entryRows: DeviationSurveyEntryRows,
  fluidPropertiesContext: FluidPropertiesContext,
  surfaceTemperatureF: number,
  bottomholeTemperatureF: number,
  intervalCasingGeometry: CasingIntervalGeometry,
  bottomCasingPressurePSIA: number,
  producingBottomholePressurePSIA: number,
  includeCasingFrictionLoss: boolean
): CasingPressureIntervalResult {
  const resolvedCasingFlow =
    correlations?.casing_flow ?? Casing_Flow_Options_Enum.BeggsBrill;
  const resolvedCasingApplyHazenWilliams =
    correlations?.casing_apply_hazen_williams_correction ?? false;
  const resolvedCasingHazenWilliamsCoefficient =
    correlations?.casing_hazen_williams_coefficient ?? 0;
  const resolvedCasingRoughnessInches =
    correlations?.casing_roughness_inches ?? 0;
  const resolvedCasingPalmerCorrection =
    correlations?.casing_palmer_correction ?? false;
  const resolvedCasingGriffithWallis =
    correlations?.casing_griffith_wallis ?? false;

  // Establish TVD/MD levels between perfs and datum
  const datumAbovePerfsTVD = datumPointTVDFt < topOfPerforationsTVDFt;
  const datumBeforePerfsMD = datumPointMDFt < topOfPerforationsMDFt;

  const perfsToDatumStartingMDFt = datumBeforePerfsMD
    ? datumPointMDFt
    : topOfPerforationsMDFt;
  const perfsToDatumEndingMDFt = datumBeforePerfsMD
    ? topOfPerforationsMDFt
    : datumPointMDFt;

  // Call computeIntervalCasingPressureProperties directly since we need to handle
  // friction and hydrostatic pressure separately, as we need to adjust the return
  // pressure based on whether the datum is before or above the perforations.
  const perfsToDatumIntervalPressureProperties =
    computeIntervalCasingPressureProperties({
      startMDFt: perfsToDatumStartingMDFt,
      endMDFt: perfsToDatumEndingMDFt,
      entryRows,
      fluidPropertiesContext,
      surfaceTemperatureF,
      bottomholeTemperatureF,
      flow: resolvedCasingFlow,
      applyHazenWilliams: resolvedCasingApplyHazenWilliams,
      hazenWilliamsCoefficient: resolvedCasingHazenWilliamsCoefficient,
      roughnessInches: resolvedCasingRoughnessInches,
      palmerCorrection: resolvedCasingPalmerCorrection,
      griffithWallis: resolvedCasingGriffithWallis,
      bottomCasingPressurePSIA,
      producingBottomholePressurePSIA,
      includeCasingFrictionLoss,
      intervalCasingGeometry
    });

  const perfsToDatumCasingFrictionPressurePSIA =
    perfsToDatumIntervalPressureProperties.frictionPressurePSIA;
  const perfsToDatumCasingHydrostaticPressurePSIA =
    perfsToDatumIntervalPressureProperties.hydrostaticPressurePSIA;

  // If datum before perfs, add in casing friction pressure else remove it
  let returnPressurePSIA = wellFlowingPressureAtDatumPSIA;
  let perfsToDatumPressurePSIAForGradient = wellFlowingPressureAtDatumPSIA;

  // If datum above perfs, add in hydrostatic pressure else remove it
  if (datumAbovePerfsTVD) {
    returnPressurePSIA += perfsToDatumCasingHydrostaticPressurePSIA;
  } else {
    returnPressurePSIA -= perfsToDatumCasingHydrostaticPressurePSIA;
  }

  // Leave casing friction out of gradient - gradient is just tvd/hydrostatic pressure
  perfsToDatumPressurePSIAForGradient = returnPressurePSIA;

  if (datumBeforePerfsMD) {
    returnPressurePSIA += perfsToDatumCasingFrictionPressurePSIA;
  } else {
    returnPressurePSIA -= perfsToDatumCasingFrictionPressurePSIA;
  }

  return {
    endingPressurePSIA: returnPressurePSIA,
    pressureForGradientPSIA: perfsToDatumPressurePSIAForGradient,
    casingFrictionProperties:
      perfsToDatumIntervalPressureProperties.casingFrictionProperties
  };
}

export function computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentDownward(
  wellFlowingPressureAtPerfsPSIA: number,
  correlations: Correlations | null | undefined,
  topOfPerforationsMDFt: number,
  topOfPerforationsTVDFt: number,
  bottomOfTubingMDFt: number,
  bottomOfTubingTVDFt: number,
  boltOnDischargeOuterDiameterIn: number,
  topOfPumpsMDFt: number,
  topOfPumpsTVDFt: number,
  bottomOfPumpsMDFt: number,
  bottomOfPumpsTVDFt: number,
  bottomOfEquipmentMDFt: number,
  bottomOfEquipmentTVDFt: number,
  topTubingOuterDiameterIn: number,
  bottomTubingOuterDiameterIn: number,
  topTubingLengthFt: number,
  primaryPumpOuterDiameterIn: number,
  shroudOuterDiameterIn: number,
  entryRows: DeviationSurveyEntryRows,
  fluidPropertiesContext: FluidPropertiesContext,
  surfaceTemperatureF: number,
  bottomholeTemperatureF: number,
  intervalCasingGeometry: CasingIntervalGeometry,
  bottomCasingPressurePSIA: number,
  producingBottomholePressurePSIA: number,
  includeCasingFrictionLoss: boolean,
  getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number
): CasingPressureIntervalResult {
  const emptyFrictionProperties = getEmptyCasingFrictionProperties();

  const resolved = resolveCasingCorrelationOptions(correlations);

  const buildSectionIntervalCasingGeometry = (
    annulusDiameterIn: number
  ): CasingIntervalGeometry => ({
    ...intervalCasingGeometry,
    annulusDiameterAtStart: annulusDiameterIn,
    annulusDiameterAtEnd: annulusDiameterIn,
    annulusDiameterRanges: []
  });

  const resolveTubingOuterDiameterAtDepth = (depthMDFt: number): number => {
    const hasTopTubing = topTubingLengthFt > 0 && topTubingOuterDiameterIn > 0;
    const hasBottomTubing = bottomTubingOuterDiameterIn > 0;

    if (hasTopTubing && depthMDFt <= topTubingLengthFt) {
      return topTubingOuterDiameterIn;
    }

    if (hasBottomTubing) {
      return bottomTubingOuterDiameterIn;
    }

    return topTubingOuterDiameterIn || bottomTubingOuterDiameterIn || 0;
  };

  const runDownflowSection = ({
    segmentStartMDFt,
    segmentEndMDFt,
    segmentEndTVDFt,
    sectionIntervalCasingGeometry,
    currentPressurePSIA
  }: {
    segmentStartMDFt: number;
    segmentStartTVDFt: number;
    segmentEndMDFt: number;
    segmentEndTVDFt: number;
    sectionIntervalCasingGeometry: CasingIntervalGeometry;
    currentPressurePSIA: number;
  }): {
    endingPressurePSIA: number;
    frictionPressurePSIA: number;
    hydrostaticPressurePSIA: number;
    casingFrictionProperties: PipeFrictionProperties;
  } => {
    const effectiveStartMDFt = Math.max(
      topOfPerforationsMDFt,
      segmentStartMDFt
    );
    if (effectiveStartMDFt >= segmentEndMDFt) {
      return {
        endingPressurePSIA: currentPressurePSIA,
        frictionPressurePSIA: 0,
        hydrostaticPressurePSIA: 0,
        casingFrictionProperties: emptyFrictionProperties
      };
    }

    const resolvedSectionAnnulusDiameterIn = resolveAnnulusDiameterForInterval(
      effectiveStartMDFt,
      segmentEndMDFt,
      sectionIntervalCasingGeometry
    );

    if ((resolvedSectionAnnulusDiameterIn ?? 0) <= 0) {
      return {
        endingPressurePSIA: currentPressurePSIA,
        frictionPressurePSIA: 0,
        hydrostaticPressurePSIA: 0,
        casingFrictionProperties: emptyFrictionProperties
      };
    }

    const sectionIntervalProperties = computeIntervalCasingPressureProperties({
      startMDFt: effectiveStartMDFt,
      endMDFt: segmentEndMDFt,
      entryRows,
      fluidPropertiesContext,
      surfaceTemperatureF,
      bottomholeTemperatureF,
      flow: resolved.flow,
      applyHazenWilliams: resolved.applyHazenWilliams,
      hazenWilliamsCoefficient: resolved.hazenWilliamsCoefficient,
      roughnessInches: resolved.roughnessInches,
      palmerCorrection: resolved.palmerCorrection,
      griffithWallis: resolved.griffithWallis,
      bottomCasingPressurePSIA,
      producingBottomholePressurePSIA,
      includeCasingFrictionLoss,
      intervalCasingGeometry: sectionIntervalCasingGeometry
    });

    const sectionAppliedFrictionPSIA = includeCasingFrictionLoss
      ? sectionIntervalProperties.frictionPressurePSIA
      : 0;

    // Section pressure should be based on the TVD reference pressure,
    // then adjusted by section friction only when enabled.
    const sectionEndingPressurePSIA =
      getPressureAtReferenceTVDPSIA(segmentEndTVDFt) -
      sectionAppliedFrictionPSIA;
    return {
      endingPressurePSIA: sectionEndingPressurePSIA,
      frictionPressurePSIA: sectionIntervalProperties.frictionPressurePSIA,
      hydrostaticPressurePSIA:
        sectionIntervalProperties.hydrostaticPressurePSIA,
      casingFrictionProperties:
        sectionIntervalProperties.casingFrictionProperties
    };
  };

  let currentPressurePSIA = wellFlowingPressureAtPerfsPSIA;
  let totalFrictionPressurePSIA = 0;
  let totalHydrostaticPressurePSIA = 0;
  let latestFrictionProperties: PipeFrictionProperties =
    emptyFrictionProperties;

  // Run tubing geometry only to the bottom of the tubing. A bolt-on discharge
  // occupies the separate interval between this boundary and the pumps.
  if (topOfPerforationsMDFt < bottomOfTubingMDFt) {
    const perfsToBottomOfTubingResult = runDownflowSection({
      segmentStartMDFt: topOfPerforationsMDFt,
      segmentStartTVDFt: topOfPerforationsTVDFt,
      segmentEndMDFt: bottomOfTubingMDFt,
      segmentEndTVDFt: bottomOfTubingTVDFt,
      sectionIntervalCasingGeometry: buildSectionIntervalCasingGeometry(
        resolveTubingOuterDiameterAtDepth(topOfPerforationsMDFt)
      ),
      currentPressurePSIA
    });
    currentPressurePSIA = perfsToBottomOfTubingResult.endingPressurePSIA;
    totalFrictionPressurePSIA +=
      perfsToBottomOfTubingResult.frictionPressurePSIA;
    totalHydrostaticPressurePSIA +=
      perfsToBottomOfTubingResult.hydrostaticPressurePSIA;
    if (perfsToBottomOfTubingResult.frictionPressurePSIA > 0) {
      latestFrictionProperties =
        perfsToBottomOfTubingResult.casingFrictionProperties;
    }
  }

  const dischargeInterval = resolveDischargeDownflowInterval(
    topOfPerforationsMDFt,
    bottomOfTubingMDFt,
    topOfPumpsMDFt,
    boltOnDischargeOuterDiameterIn,
    primaryPumpOuterDiameterIn
  );
  if (dischargeInterval) {
    const dischargeResult = runDownflowSection({
      segmentStartMDFt: dischargeInterval.startMDFt,
      segmentStartTVDFt: bottomOfTubingTVDFt,
      segmentEndMDFt: dischargeInterval.endMDFt,
      segmentEndTVDFt: topOfPumpsTVDFt,
      sectionIntervalCasingGeometry: buildSectionIntervalCasingGeometry(
        dischargeInterval.annulusDiameterIn
      ),
      currentPressurePSIA
    });
    currentPressurePSIA = dischargeResult.endingPressurePSIA;
    totalFrictionPressurePSIA += dischargeResult.frictionPressurePSIA;
    totalHydrostaticPressurePSIA += dischargeResult.hydrostaticPressurePSIA;
    if (dischargeResult.frictionPressurePSIA > 0) {
      latestFrictionProperties = dischargeResult.casingFrictionProperties;
    }
  }

  // See if we need to run the downflow section in the pumps section by
  // seeing if the perfs are within that pump range
  if (topOfPerforationsMDFt < bottomOfPumpsMDFt) {
    let segmentStartMDFt = topOfPerforationsMDFt;
    let segmentStartTVDFt = topOfPerforationsTVDFt;
    // We only want to compute for the length of the pumps in this section
    // so we adjust the segment start to the top of the pumps if it is above them
    if (segmentStartMDFt < topOfPumpsMDFt) {
      segmentStartMDFt = topOfPumpsMDFt;
      segmentStartTVDFt = topOfPumpsTVDFt;
    }
    const topOfPumpsToBottomOfPumpsResults = runDownflowSection({
      segmentStartMDFt,
      segmentStartTVDFt,
      segmentEndMDFt: bottomOfPumpsMDFt,
      segmentEndTVDFt: bottomOfPumpsTVDFt,
      sectionIntervalCasingGeometry: buildSectionIntervalCasingGeometry(
        primaryPumpOuterDiameterIn
      ),
      currentPressurePSIA
    });
    currentPressurePSIA = topOfPumpsToBottomOfPumpsResults.endingPressurePSIA;
    totalFrictionPressurePSIA +=
      topOfPumpsToBottomOfPumpsResults.frictionPressurePSIA;
    totalHydrostaticPressurePSIA +=
      topOfPumpsToBottomOfPumpsResults.hydrostaticPressurePSIA;
    if (topOfPumpsToBottomOfPumpsResults.frictionPressurePSIA > 0) {
      latestFrictionProperties =
        topOfPumpsToBottomOfPumpsResults.casingFrictionProperties;
    }
  }

  // See how long we need to run the downflow section with the shroud
  // If perfs are before the shroud we just set it to the shroud beginning
  // Because we only want the section of the well that is within the shroud
  if (topOfPerforationsMDFt < bottomOfEquipmentMDFt) {
    let segmentStartMDFt = topOfPerforationsMDFt;
    let segmentStartTVDFt = topOfPerforationsTVDFt;
    if (segmentStartMDFt < bottomOfPumpsMDFt) {
      segmentStartMDFt = bottomOfPumpsMDFt;
      segmentStartTVDFt = bottomOfPumpsTVDFt;
    }
    const topOfShroudToBottomOfEquipmentResult = runDownflowSection({
      segmentStartMDFt,
      segmentStartTVDFt,
      segmentEndMDFt: bottomOfEquipmentMDFt,
      segmentEndTVDFt: bottomOfEquipmentTVDFt,
      sectionIntervalCasingGeometry: buildSectionIntervalCasingGeometry(
        shroudOuterDiameterIn
      ),
      currentPressurePSIA
    });
    currentPressurePSIA =
      topOfShroudToBottomOfEquipmentResult.endingPressurePSIA;
    totalFrictionPressurePSIA +=
      topOfShroudToBottomOfEquipmentResult.frictionPressurePSIA;
    totalHydrostaticPressurePSIA +=
      topOfShroudToBottomOfEquipmentResult.hydrostaticPressurePSIA;
    if (topOfShroudToBottomOfEquipmentResult.frictionPressurePSIA > 0) {
      latestFrictionProperties =
        topOfShroudToBottomOfEquipmentResult.casingFrictionProperties;
    }
  }

  const casingFrictionProperties: PipeFrictionProperties = {
    ...(latestFrictionProperties ?? emptyFrictionProperties),
    frictionPressurePSIA: totalFrictionPressurePSIA,
    hydrostaticPressurePSIA: totalHydrostaticPressurePSIA,
    intermediates: {
      ...(latestFrictionProperties?.intermediates ??
        emptyFrictionProperties.intermediates),
      frictionPressurePSIA: totalFrictionPressurePSIA
    }
  };

  return {
    endingPressurePSIA: currentPressurePSIA,
    casingFrictionProperties
  };
}

export function resolveDischargeDownflowInterval(
  topOfPerforationsMDFt: number,
  bottomOfTubingMDFt: number,
  topOfPumpsMDFt: number,
  boltOnDischargeOuterDiameterIn: number,
  primaryPumpOuterDiameterIn: number
):
  | {
      startMDFt: number;
      endMDFt: number;
      annulusDiameterIn: number;
    }
  | undefined {
  const startMDFt = Math.max(topOfPerforationsMDFt, bottomOfTubingMDFt);
  if (startMDFt >= topOfPumpsMDFt) return undefined;

  return {
    startMDFt,
    endMDFt: topOfPumpsMDFt,
    annulusDiameterIn:
      boltOnDischargeOuterDiameterIn || primaryPumpOuterDiameterIn
  };
}

export function computeCasingPressurePropertiesBetweenPerfsAndBottomOfEquipmentUpward(
  correlations: Correlations | null | undefined,
  topOfPerforationsMDFt: number,
  topOfPerforationsTVDFt: number,
  bottomOfEquipmentMDFt: number,
  bottomOfEquipmentTVDFt: number,
  entryRows: DeviationSurveyEntryRows,
  fluidPropertiesContext: FluidPropertiesContext,
  surfaceTemperatureF: number,
  bottomholeTemperatureF: number,
  intervalCasingGeometry: CasingIntervalGeometry,
  bottomCasingPressurePSIA: number,
  producingBottomholePressurePSIA: number
): PipeFrictionProperties {
  return computeCasingFrictionPropertiesAcrossEquipmentIntervalUpward({
    correlations,
    startMDFt: topOfPerforationsMDFt,
    endMDFt: bottomOfEquipmentMDFt,
    entryRows,
    fluidPropertiesContext,
    surfaceTemperatureF,
    bottomholeTemperatureF,
    intervalCasingGeometry,
    bottomCasingPressurePSIA,
    producingBottomholePressurePSIA
  });
}
